import { useEffect, useMemo, useState, useCallback, useRef } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { useNavigate, useParams } from 'react-router-dom'
import { yupResolver } from '@hookform/resolvers/yup'

import FileInputUploadField from 'components/styledFormElements/FileInputUploadField'
import { SmoothTransition } from 'components/pageTransition'
import { Loader } from 'components/loader'
import { useNotificationBar } from 'context/notificationContext'
import { getErrorResponse, getImageSrc, getRelativeIconPath } from 'services/util'
import { useCollectionContent, useAddCollection, useUpdateCollection } from 'hooks/collection'
import FormSelect from 'components/forms/FormSelect'
import { useGetTemplatesData } from 'hooks/templates'
import { EpisodeForm } from 'components/collectionForm'
import Tooltip from 'components/styledFormElements/Tooltip'
import { FileSizeLimit } from 'constants/file-validation'
import { collectionSchema } from './validation'
import { normalizeLabel, parseLabels, createPreview } from './helper'
import InputField from 'components/styledFormElements/InputField'
import LabelsList from 'components/label/LabelList'
import { ConfirmModal } from 'components/confirmationPopup'
import IconDropdown from 'components/styledFormElements/IconDropdown'
import { X } from 'lucide-react'
import { useActiveAIPrompt } from 'hooks/ai-settings'
import { useGenerateLabelsFromText } from 'hooks/ai-label-generation'
import { SparklesIcon } from '@heroicons/react/24/outline'
// -------------------------------------------------------
// Types
// -------------------------------------------------------
export interface ShowForm {
    name: string
    labels: string[]
    shortDescription: string
    longDescription: string
    publishStatus: string
    templateId: string
    thumbnail: File | string | null
    bannerImage: File | string | null
    cardImage2: File | string | null
    cardImage1: File | string | null
    status: boolean
    // icon: File | string | null
    icon: string | null
    episodes: Options[]
    shows: Options[]
    entities: any
}

interface Options {
    label: string
    value: number
    avatar?: string
}

interface ImageDataUrls {
    thumbnailFile: string | null
    thumbnailPreview: string | null
    bannerFile: string | null
    bannerPreview: string | null
    cardImage1: string | null
    cardImagePreview: string | null
    cardImage2: string | null
    iconFile: string | null
    iconPreview: string | null
    cardImagePreview2: string | null
    existingBanner: string | null
    existingThumbnail: string | null
    existingCardImage: string | null
    existingCardImage2: string | null
    existingIcon: string | null
}

// -------------------------------------------------------
// Constants
// -------------------------------------------------------
const DEFAULT_FORM_VALUES: ShowForm = {
    name: '',
    labels: [],
    shortDescription: '',
    longDescription: '',
    publishStatus: 'UNPUBLISHED',
    templateId: '0',
    thumbnail: null,
    bannerImage: null,
    cardImage2: null,
    cardImage1: null,
    icon: null,
    status: true,
    episodes: [],
    shows: [],
    entities: [],
}

const DEFAULT_IMAGE_DATA: ImageDataUrls = {
    thumbnailFile: null,
    thumbnailPreview: null,
    bannerFile: null,
    bannerPreview: null,
    cardImage1: null,
    cardImagePreview: null,
    cardImage2: null,
    cardImagePreview2: null,
    existingBanner: null,
    existingThumbnail: null,
    existingCardImage: null,
    existingCardImage2: null,
    iconFile: null,
    iconPreview: null,
    existingIcon: null,
}

// -------------------------------------------------------
// Main Component
// -------------------------------------------------------
export const AddUpdateCollection = () => {
    const params = useParams()
    const navigate = useNavigate()
    const notification = useNotificationBar()
    const [searchTerm, setSearchTerm] = useState('')

    // Form
    const methods = useForm<ShowForm>({
        defaultValues: DEFAULT_FORM_VALUES,
        resolver: yupResolver(collectionSchema),
    })

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        clearErrors,
        setError,
        watch,
        control,
        formState: { errors },
    } = methods

    // State
    const [labelInput, setLabelInput] = useState('')
    const [imageDataUrls, setImageDataUrls] =
        useState<ImageDataUrls>(DEFAULT_IMAGE_DATA)
    // const [prefillEpisodes, setPrefillEpisodes] = useState<Options[]>([])
    // const [prefillShows, setPrefillshows] = useState<Options[]>([])
    const [allEntities, setAllEntities] = useState<any[]>([])
    const [episodeLabels, setEpisodeLabels] = useState<
        Record<string, string[]>
    >({})
    const [open, setOpen] = useState(false)
    // Watched values
    const watchedThumbnail = useWatch({ control, name: 'thumbnail' })
    const watchedBanner = useWatch({ control, name: 'bannerImage' })
    const watchedCardImage1 = useWatch({ control, name: 'cardImage1' })
    const watchedCardImage2 = useWatch({ control, name: 'cardImage2' })
    const watchedIcon = useWatch({ control, name: 'icon' })
    const labelsArray = methods.watch('labels')
    const watchedName = useWatch({ control, name: 'name' })
    const watchedShortDescription = useWatch({ control, name: 'shortDescription' })

    // AI Hooks
    const { data: activePrompt } = useActiveAIPrompt()
    const generateLabelsFromText = useGenerateLabelsFromText()
    const [isGeneratingLabels, setIsGeneratingLabels] = useState(false)
    const [aiGeneratedLabels, setAiGeneratedLabels] = useState<string[]>([])
    const previousCollectionNameRef = useRef<string>('')

    let {
        data: templteData,
        isFetching: isTemplateLoading,
        refetch,
        isError,
        isSuccess,
        error,
    } = useGetTemplatesData(
        {
            fields: [
                { value: 1, id: 'status' },
                { value: 'COLLECTION', id: 'template_type' },
            ],
        },
        {
            keepPreviousData: true,
        }
    )

    // API Hooks
    const { data: showData, isFetching: loadingShow } = useCollectionContent(
        params.id,
        { enabled: !!params.id }
    )

    const { mutate: addShow, isLoading: adding } = useAddCollection()
    const { mutate: updateShow, isLoading: updating } = useUpdateCollection()

    const loading = adding || updating

    const rawList = useMemo(() => {
        if (!templteData?.data) return []
        return Array.isArray(templteData.data)
            ? templteData.data
            : templteData.data.results || []
    }, [templteData])

    const handleLabelAdd = () => {
        const token = labelInput.trim()
        if (!token) return

        const formatted = normalizeLabel(token)
        const current = methods.getValues('labels')

        if (current.some(label => label.toLowerCase() === formatted.toLowerCase())) return

        const next = [...current, formatted]

        methods.setValue('labels', next, { shouldValidate: true })
        setLabelInput('')
    }


    const handleLabelRemove = (index: number) => {
        const current = methods.getValues('labels')
        const removedLabel = current[index]
  const collectionName = watchedName?.trim()
  
  // Prevent removing the collection name label
  if (collectionName && normalizeLabel(collectionName) === removedLabel) {
    notification.push({
      title: 'Info',
      type: 'info',
      message: 'Collection name label cannot be removed'
    })
    return
  }
  
  const next = current.filter((_, i) => i !== index)

  // Update AI labels state if an AI-generated label was removed
  if (removedLabel && aiGeneratedLabels.some(l => l.toLowerCase() === removedLabel.toLowerCase())) {
    setAiGeneratedLabels(aiGeneratedLabels.filter(label => label.toLowerCase() !== removedLabel.toLowerCase()))
  }

        methods.setValue('labels', next, { shouldValidate: true })
    }

const handleGenerateLabels = async () => {
  const title = watchedName
  const description = watchedShortDescription
  
  if (!title.trim()) {
    notification.push({
      title: 'Error',
      type: 'error',
      message: 'Collection name is required for AI label generation'
    })
    return
  }

  if (!description || !description.trim()) {
    notification.push({
      title: 'Error',
      type: 'error',
      message: 'Short description is required for AI label generation'
    })
    return
  }

  setIsGeneratingLabels(true)
  
  try {
    if (!activePrompt) {
      notification.push({
        title: 'Error',
        type: 'error',
        message: 'No active AI prompt found. Please configure one in Settings.'
      })
      return
    }

    const result = await generateLabelsFromText.mutateAsync({
      title,
      description,
      prompt_text: activePrompt.prompt
    })
    
    const labels = (result as any)?.data?.results?.labels || (result as any)?.results?.labels || (result as any)?.labels
    
    if (labels && Array.isArray(labels)) {
      const normalizedLabels = labels
        .filter((label: string) => label && label.trim())
        .map((label: string) => normalizeLabel(label))
        .filter((label: string, index: number, self: string[]) => 
          self.findIndex(l => l.toLowerCase() === label.toLowerCase()) === index // Remove duplicates
        )

      // Preserve collection name label
      const collectionName = watchedName?.trim()
      const collectionNameLabel = collectionName ? normalizeLabel(collectionName) : ''
      
      // Remove collection name from AI labels if it exists (to avoid duplicate) - case insensitive
      const aiLabelsWithoutCollectionName = normalizedLabels.filter(label => 
        label.toLowerCase() !== collectionNameLabel.toLowerCase()
      )
      
      // Store AI-generated labels separately
      const finalAiLabels = collectionNameLabel 
        ? [collectionNameLabel, ...aiLabelsWithoutCollectionName]
        : aiLabelsWithoutCollectionName

      setAiGeneratedLabels(finalAiLabels)

      // Get current labels and separate non-AI labels (manual labels)
      const currentLabels = methods.getValues('labels') || []
      const nonAiLabels = currentLabels.filter(
        (label) => !aiGeneratedLabels.some(ai => ai.toLowerCase() === label.toLowerCase())
      )

      // Combine: non-AI labels + new AI labels
      const combinedLabels = [
        ...nonAiLabels,
        ...finalAiLabels.filter((l) => !nonAiLabels.some(n => n.toLowerCase() === l.toLowerCase()))
      ]

      setValue('labels', combinedLabels, { shouldValidate: true })

      notification.push({
        title: 'Success',
        type: 'success',
        message: result?.data?.message || 'Labels generated successfully'
      })
    }
  } catch (error) {
    notification.push({
      title: 'Error',
      type: 'error',
      message: 'Failed to generate labels. Please try again.'
    })
  } finally {
    setIsGeneratingLabels(false)
  }
}

  // Auto-add collection name as label
  useEffect(() => {
    const collectionName = watchedName?.trim()
    const normalizedName = collectionName ? normalizeLabel(collectionName) : ''
    const previousNormalizedName = previousCollectionNameRef.current
    
    // Only update if the normalized name has actually changed
    if (normalizedName !== previousNormalizedName) {
      const currentLabels = methods.getValues('labels') || []
      
      // Remove the old collection name label if it exists
      const withoutOldName = previousNormalizedName 
        ? currentLabels.filter(label => label !== previousNormalizedName)
        : currentLabels
      
      // Add the new collection name label at the beginning (if not empty and not already present) - case insensitive check
      const updatedLabels = normalizedName 
        ? (withoutOldName.some(label => label.toLowerCase() === normalizedName.toLowerCase())
            ? withoutOldName 
            : [normalizedName, ...withoutOldName])
        : withoutOldName
      
      methods.setValue('labels', updatedLabels, { shouldValidate: true })
      
      // Update the ref to track the current collection name
      previousCollectionNameRef.current = normalizedName
    }
  }, [watchedName, methods])

  // Remove duplicate labels whenever labels change
  useEffect(() => {
    if (!labelsArray || labelsArray.length === 0) return
    
    // Case-insensitive duplicate removal
    const uniqueLabels = labelsArray.filter((label: string, index: number, self: string[]) => 
      self.findIndex(l => l.toLowerCase() === label.toLowerCase()) === index
    )
    
    // Only update if duplicates were found
    if (uniqueLabels.length !== labelsArray.length) {
      methods.setValue('labels', uniqueLabels, { shouldValidate: true })
    }
  }, [labelsArray, methods])

    const handleImageSelect = useCallback(
        (
            field: keyof ShowForm,
            file: any,
            previewField: keyof ImageDataUrls,
            existingField: keyof ImageDataUrls
        ) => {
            if (!file) {
                setValue(field, null, { shouldDirty: true, shouldValidate: true })
                clearErrors(field)

                setImageDataUrls((prev) => ({
                    ...prev,
                    [previewField]: null,
                    [existingField]: null,
                }))

                return
            }

            setValue(field, file)
            clearErrors(field)

            const previewUrl = URL.createObjectURL(file)
            setImageDataUrls((prev) => ({
                ...prev,
                [previewField]: previewUrl,
            }))

            // Cleanup function for the preview URL
            return () => URL.revokeObjectURL(previewUrl)
        },
        [setValue, clearErrors]
    )

    // -------------------------------------------------------
    // Effects
    // -------------------------------------------------------
    // Prefill form when editing
    useEffect(() => {
        if (!params.id || !showData?.data?.collection) return

        const { collection } = showData.data
        const labels = parseLabels(collection.labels)

        // Prepare episode options
        const episodeOptions: Options[] = collection?.episodes?.map(
            (e: any) => ({
                name: e.episode.name,
                value: e.episode.id,
                thumbnail: e.episode?.thumbnail,
                id: e.episode.id,
                labels: e.episode?.labels,
                type: 'episode',
            })
        )
        const showOptions: Options[] = collection?.shows?.map((e: any) => ({
            name: e.show.name,
            value: e.show.id,
            thumbnail: e.show?.thumbnail,
            id: e.show.id,
            labels: e.show?.labels,
            type: 'show',
        }))

        const allEntities = collection?.entities?.map((e: any) =>
            e?.type === 'show'
                ? {
                    name: e.show.name,
                    value: e.show.id,
                    thumbnail: e.show?.thumbnail,
                    id: e.show.id,
                    labels: e.show?.labels,
                    type: 'show',
                }
                : {
                    name: e.episode.name,
                    value: e.episode.id,
                    thumbnail: e.episode?.thumbnail,
                    id: e.episode.id,
                    labels: e.episode?.labels,
                    type: 'episode',
                }
        )

        // Reset form with fetched data
        reset({
            name: collection.name,
            labels: labels,
            shortDescription: collection.shortDescription,
            longDescription: collection.longDescription,
            publishStatus: collection.publishStatus,
            templateId: collection.templateId,
            thumbnail: collection.thumbnail,
            bannerImage: collection.bannerImage,
            cardImage2: collection.cardImage2,
            cardImage1: collection.cardImage1,
            icon: collection.icon,
            status: !!collection.status,
            episodes: episodeOptions,
            shows: showOptions,
            entities: allEntities,
        })

        // Update local state
        setAllEntities(allEntities)
        // Set existing image URLs
        setImageDataUrls((prev) => ({
            ...prev,
            existingThumbnail: collection.thumbnail,
            existingBanner: collection.bannerImage,
            existingCardImage: collection.cardImage1,
            existingCardImage2: collection.cardImage2,
            existingIcon: collection.icon,
        }))
        
        // Initialize episodeLabels map for tracking entity-specific labels
        const initialEpisodeLabelsMap: Record<string, string[]> = {}
        allEntities?.forEach((entity: any) => {
            const labels = parseLabels(entity.labels)
            if (labels.length) {
                initialEpisodeLabelsMap[entity.id] = labels
            }
        })
        setEpisodeLabels(initialEpisodeLabelsMap)
        
        // Initialize previousCollectionNameRef to prevent duplicate label on edit
        previousCollectionNameRef.current = collection.name ? normalizeLabel(collection.name) : ''
    }, [showData, params.id, reset])

    // Image preview effects
    useEffect(() => {
        return createPreview(
            watchedThumbnail,
            setImageDataUrls,
            'thumbnailPreview'
        )
    }, [watchedThumbnail])

    useEffect(() => {
        return createPreview(watchedBanner, setImageDataUrls, 'bannerPreview')
    }, [watchedBanner])

    useEffect(() => {
        return createPreview(
            watchedCardImage1,
            setImageDataUrls,
            'cardImagePreview'
        )
    }, [watchedCardImage1])

    useEffect(() => {
        return createPreview(
            watchedCardImage2,
            setImageDataUrls,
            'cardImagePreview2'
        )
    }, [watchedCardImage2])

    useEffect(() => {
        return createPreview(watchedIcon, setImageDataUrls, 'iconPreview')
    }, [watchedIcon])

    // Cleanup image previews on unmount
    useEffect(() => {
        return () => {
            Object.values(imageDataUrls).forEach((url) => {
                if (url && url.startsWith('blob:')) {
                    URL.revokeObjectURL(url)
                }
            })
        }
    }, [])
    const handleEpisodesChange = useCallback(
        (updatedEpisodes: any[]) => {
            const episodeLabelsMap: Record<string, string[]> = {}
            const allEpisodeLabels: string[] = []
            const currentEpisodeIds: string[] = []

            updatedEpisodes.forEach((ep: any) => {
                if (!ep) return

                const episodeId = ep.id
                currentEpisodeIds.push(episodeId)

                const labels = parseLabels(ep.labels)

                episodeLabelsMap[episodeId] = labels

                labels.forEach((l) => {
                    if (!allEpisodeLabels.some(existing => existing.toLowerCase() === l.toLowerCase())) {
                        allEpisodeLabels.push(l)
                    }
                })
            })

            // Find deleted episode labels
            const deletedEpisodeLabels: string[] = []

            Object.keys(episodeLabels).forEach((epId) => {
                if (!currentEpisodeIds.includes(epId)) {
                    episodeLabels[epId]?.forEach((label) => {
                        const stillUsed = Object.values(episodeLabelsMap).some(
                            (existing) => existing.some(l => l.toLowerCase() === label.toLowerCase())
                        )

                        if (!stillUsed && !deletedEpisodeLabels.some(l => l.toLowerCase() === label.toLowerCase())) {
                            deletedEpisodeLabels.push(label)
                        }
                    })
                }
            })

            setEpisodeLabels(episodeLabelsMap)

            // Manual labels from RHF
            const currentLabels = methods.getValues('labels') || []

            const manualLabels = currentLabels.filter(
                (label) =>
                    !deletedEpisodeLabels.some(d => d.toLowerCase() === label.toLowerCase()) &&
                    !allEpisodeLabels.some(e => e.toLowerCase() === label.toLowerCase())
            )

            const combinedLabels = manualLabels.concat(
                allEpisodeLabels.filter((l) => !manualLabels.some(m => m.toLowerCase() === l.toLowerCase()))
            )

            methods.setValue('labels', combinedLabels, {
                shouldValidate: true,
                shouldDirty: true,
            })
        },
        [episodeLabels, methods]
    )

    const handleCancel = useCallback(() => setOpen(true), [])
    // -------------------------------------------------------
    // Submit Handler
    // -------------------------------------------------------
    const onSubmit = useCallback(
        (values: ShowForm) => {
            const formData = new FormData()

            // Extract episode IDs
            const showIds = values?.entities
                .filter((e: any) => e.type === 'show')
                .map((e: any, i: number) => ({
                    id: e.id,
                    sortOrder: i + 1,
                }));

            // Extract episode IDs
            const episodeIds = values?.entities
                .filter((e: any) => e.type === 'episode')
                .map((e: any, i: number) => ({
                    id: e.id,
                    sortOrder: i + 1,
                }));

            const entities = values?.entities.map((e: any, i) => ({
                id: e.id,
                type: e.type,
                sortOrder: i + 1,
            }))

            formData.append(
                'contentData',
                JSON.stringify({
                    episodes: episodeIds,
                    shows: showIds,
                    entities: entities,
                })
            )

            // Basic fields
            formData.append('name', values.name)
            formData.append('shortDescription', values.shortDescription)
            formData.append('longDescription', values.longDescription)
            formData.append('templateId', values.templateId)
            // Labels
            if (labelsArray.length) {
                formData.append('labels', JSON.stringify(labelsArray))
            }

            // Validate required images for new collections

            // Append image files if they exist
            if (values.thumbnail instanceof File)
                formData.append('thumbnail', values.thumbnail)
            if (values.bannerImage instanceof File)
                formData.append('bannerImage', values.bannerImage)
            if (values.cardImage1 instanceof File)
                formData.append('cardImage1', values.cardImage1)
            if (values.cardImage2 instanceof File)
                formData.append('cardImage2', values.cardImage2)

            // if (values.icon instanceof File)
            //     formData.append('icon', values.icon)



            if (!values.icon && !imageDataUrls.existingIcon) {
                return setError('icon', {
                    message: 'Icon is required',
                })
            }
            if (!values.thumbnail && !imageDataUrls.existingThumbnail) {
                return setError('thumbnail', {
                    message: 'Thumbnail is required',
                })
            }
            if (!values.cardImage1 && !imageDataUrls.existingCardImage) {
                return setError('cardImage1', {
                    message: 'Card Image-1 is required',
                })
            }

            if (!values.cardImage2 && !imageDataUrls.existingCardImage2) {
                return setError('cardImage2', {
                    message: 'Card Image-2 is required',
                })
            }


            const iconPath = values?.icon ? getRelativeIconPath(values.icon) : null;
            if (iconPath && typeof iconPath === 'string' && iconPath.trim() !== '') {
            formData.append('icon', iconPath);
            }

            const onError = (errorRes: any) => {
                notification.push(getErrorResponse(errorRes))
            }

            const onSuccess = () => {
                notification.push({
                    title: params.id
                        ? 'Collection Updated'
                        : 'Collection Added',
                    type: 'success',
                    message: `${values.name} ${params.id ? 'updated' : 'added'
                        } successfully`,
                })
                navigate(-1)
            }

            if (params.id) {
                updateShow(
                    { id: params.id, data: formData },
                    { onSuccess, onError }
                )
            } else {
                addShow({ data: formData }, { onSuccess, onError })
            }
        },
        [
            params.id,
            labelsArray,
            imageDataUrls,
            notification,
            navigate,
            addShow,
            updateShow,
            setError,
        ]
    )

    // -------------------------------------------------------
    // Render
    // -------------------------------------------------------
    return (
        <div className="w-full px-6 py-6 bg-gray-50 dark:bg-slate-900">
            {/* PAGE HEADER */}
            <div className="mb-8">
                <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                    {params.id ? 'Edit Collection' : 'Create New Collection'}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Manage collection details, images and linked entities
                </p>
            </div>

            <ConfirmModal
                open={open}
                title="Are you sure?"
                message="This will remove all the data you have entered. This action cannot be undone."
                confirmText="Confirm"
                cancelText="Cancel"
                variant="danger"
                onCancel={() => setOpen(false)}
                onConfirm={() => {
                    setOpen(false)
                    navigate(-1)
                }}
            />

            {/* LOADER */}
            {loadingShow || loading || isTemplateLoading ? (
                <div className="flex justify-center items-center min-h-[60vh]">
                    <Loader />
                </div>
            ) : (
                <SmoothTransition>
                    <FormProvider {...methods}>
                        <form
                            onSubmit={handleSubmit(onSubmit)}
                            className="space-y-8"
                        >
                            {/* BASIC INFORMATION */}
                            <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
                                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                                    Basic Information
                                </h4>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <InputField
                                        label="Collection Name"
                                        required
                                        helpText='Title of the collection grouping multiple shows. Used to organize content by theme, genre, or custom categories.'
                                        placeholder="Enter Collection Name"
                                        type="text"
                                        register={register("name")}
                                        error={errors?.name?.message}
                                    />


                                    <FormSelect
                                        label="Select Template"
                                        name="templateId"
                                        options={rawList}
                                        register={register('templateId', {
                                            required: 'Template is required',
                                            valueAsNumber: true,
                                        })}
                                        defaultOptionLabel="Default"
                                        errors={errors as any}
                                        helpText='Identifier for the layout template applied to this collection. Controls how shows are displayed within the collection.'
                                    />

                                    {/* DESCRIPTION INSIDE BASIC INFO */}

                                    <InputField
                                        label="Short Description"
                                        required
                                        helpText='Brief overview of the collection (min-15 characters and max 150 characters). Displayed in search results and collection listings to attract viewers.
'
                                        placeholder="Enter Short Description "
                                        type="text"
                                        register={register('shortDescription', {
                                            required: 'Short Description is required',
                                        })}
                                        error={errors?.shortDescription?.message}
                                    />
                                    <InputField
                                        label="Long Description"
                                        helpText='Detailed description of the collection plot, characters, and themes. Displayed on the collection detail page for more context'
                                        placeholder="Enter Long Description "
                                        type="text"
                                        register={register('longDescription')}
                                        error={errors?.longDescription?.message}
                                        row={1}
                                        isTextarea={true}
                                        texAreaClass="max-h-20 min-h-10"
                                    />

                                    {/* Label + Tooltip */}
                                    <div className="flex flex-col space-y-1">
                                        {/* Label + Tooltip */}
                                        <div className="flex items-center gap-2">
                                            <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                                Labels
                                            </label>
                                            <span className="text-red-700">*</span>
                                            <Tooltip
                                                items={[
                                                    'Tags to categorize collections (e.g., Featured,New Releases, Trending).',
                                                    'Aids in content discovery and homepage organization.',
                                                ]}
                                            />
                                        </div>

                                        {/* Input box (same as before) */}
                                        <input
                                            value={labelInput}
                                            onChange={(e) => setLabelInput(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault()
                                                    handleLabelAdd()
                                                }
                                            }}
                                            className="w-full rounded px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-700 dark:text-white text-sm"
                                            placeholder="Type label and press Enter"
                                        />

                            {/* AI Label Generation Button */}
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    title={!watchedName?.trim() ? "Collection name is required" : !watchedShortDescription?.trim() ? "Short description is required" : "Generate labels using AI"}
                                    onClick={handleGenerateLabels}
                                    disabled={isGeneratingLabels || !watchedName?.trim() || !watchedShortDescription?.trim()}
                                    className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2 text-sm"
                                >
                                    <SparklesIcon className="h-4 w-4" />
                                    {isGeneratingLabels ? 'Generating...' : 'Generate Labels'}
                                </button>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                    AI will use name and short description
                                </span>
                            </div>

                                        {/* Labels container (textarea-like) */}
                                        <div className="border border-gray-300 dark:border-gray-600 rounded p-2 max-h-40 overflow-y-auto bg-gray-50 dark:bg-slate-800">
                                            {labelsArray?.length > 0 ? (
                                                <div className="grid grid-cols-1 md:grid-cols gap-2">
                                                    <LabelsList
                                                        labels={labelsArray}
                                                        onRemove={handleLabelRemove}
                                                    />
                                                </div>
                                            ) : (
                                                <span className="text-xs text-gray-400">
                                                    No labels added
                                                </span>
                                            )}
                                        </div>

                                        {/* Error */}
                                        {errors?.labels && (
                                            <span className="text-xs text-red-500">
                                                {errors.labels.message}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {/* MEDIA + ENTITIES SIDE BY SIDE */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                {/* MEDIA ASSETS (LEFT - 50%) */}
                                <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-6 shadow-sm">
                                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
                                        Media Assets
                                    </h4>

                                    <div className="grid grid-cols-1 gap-6">
                                        {/* <FileInputUploadField
                                            label="Icon"
                                            fieldName="icon"
                                             register={register('icon') as any}
                                            errors={errors}
                                            existingFile={
                                                imageDataUrls.existingIcon
                                            }
                                            watchFile={
                                                watchedIcon as any
                                            }
                                            onFileSelect={(file) =>
                                                handleImageSelect(
                                                    'icon',
                                                    file,
                                                    'iconPreview',
                                                    'existingIcon',
                                                )
                                            }
                                            allowedTypes={FileSizeLimit.icon.allowedTypes}
                                            preview={imageDataUrls.iconPreview}
                                            tooltip={FileSizeLimit.icon.tooltipItems as any}
                                            fileSizeLimit={FileSizeLimit?.icon}
                                            required
                                        /> */}

                                        <div className="flex flex-col gap-2 w-full">
                                            <Controller
                                                name="icon"
                                                control={control}
                                                render={({ field }) => {
                                                    const iconValue = field.value || imageDataUrls.existingIcon;

                                                    return (
                                                        <div className="relative">
                                                            {/* If icon selected → show inside field */}
                                                            {iconValue ? (
                                                                <div className="flex items-center justify-between border border-gray-300 dark:border-slate-600 rounded-md px-3 py-2 bg-white dark:bg-slate-800">
                                                                    <div className="flex items-center gap-2">
                                                                        <img
                                                                            src={
                                                                                iconValue.startsWith('http')
                                                                                    ? iconValue
                                                                                    : getImageSrc(iconValue)
                                                                            }
                                                                            alt="Selected icon"
                                                                            className="w-6 h-6 object-contain"
                                                                        />
                                                                        <span className="text-sm text-gray-700 dark:text-gray-200">
                                                                            Icon selected
                                                                        </span>
                                                                    </div>

                                                                    {/* Remove icon */}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => {
                                                                            field.onChange('');
                                                                            // setIconPreview(null);
                                                                            setImageDataUrls((prev) => ({
                                                                                ...prev,
                                                                                existingIcon: null,
                                                                            }))
                                                                            clearErrors('icon');
                                                                        }}
                                                                        className="ml-2 p-1 bg-black/70 hover:bg-black text-white rounded-full"
                                                                    >
                                                                        <X size={14} />
                                                                    </button>
                                                                </div>
                                                            ) : (
                                                                /* Show dropdown when no icon selected */
                                                                <IconDropdown
                                                                    value={iconValue}
                                                                    onChange={(selectedIcon: any) => {
                                                                        const iconKey =
                                                                            selectedIcon?.path ||
                                                                            selectedIcon?.image ||
                                                                            selectedIcon?.icon ||
                                                                            '';

                                                                        field.onChange(iconKey);

                                                                        if (iconKey) {
                                                                            const preview = iconKey.startsWith('http')
                                                                                ? iconKey
                                                                                : getImageSrc(iconKey);

                                                                            // setIconPreview(preview);
                                                                            setImageDataUrls((prev) => ({
                                                                                ...prev,
                                                                                existingIcon: preview,
                                                                            }))
                                                                            clearErrors('icon');
                                                                        } else {
                                                                            // setIconPreview(null);
                                                                            setImageDataUrls((prev) => ({
                                                                                ...prev,
                                                                                existingIcon: null,
                                                                            }))
                                                                        }
                                                                    }}
                                                                />
                                                            )}
                                                        </div>
                                                    );
                                                }}
                                            />

                                            {errors.icon && (
                                                <p className="text-xs text-red-600">{errors.icon.message}</p>
                                            )}
                                        </div>

                                        <FileInputUploadField
                                            label="Thumbnail Image"
                                            fieldName="thumbnail"
                                            register={register('thumbnail') as any}
                                            errors={errors}
                                            existingFile={
                                                imageDataUrls.existingThumbnail
                                            }
                                            watchFile={
                                                watchedThumbnail as
                                                | File
                                                | null
                                                | any
                                            }
                                            onFileSelect={(file) =>
                                                handleImageSelect(
                                                    'thumbnail',
                                                    file,
                                                    'thumbnailPreview',
                                                    'existingThumbnail',
                                                )
                                            }
                                            allowedTypes={FileSizeLimit.thumbnail.allowedTypes}
                                            preview={
                                                imageDataUrls.thumbnailPreview
                                            }
                                            tooltip={FileSizeLimit.thumbnail.tooltipItems as any}
                                            fileSizeLimit={FileSizeLimit?.thumbnail}
                                            required
                                        />

                                        <FileInputUploadField
                                            label="Card Image (1)"
                                            fieldName="cardImage1"
                                            register={register(
                                                'cardImage1') as any}
                                            errors={errors}
                                            existingFile={
                                                imageDataUrls.existingCardImage
                                            }
                                            watchFile={
                                                watchedCardImage1 as
                                                | File
                                                | null
                                                | any
                                            }
                                            onFileSelect={(file) =>
                                                handleImageSelect(
                                                    'cardImage1',
                                                    file,
                                                    'cardImagePreview',
                                                    'existingCardImage',
                                                )
                                            }
                                            allowedTypes={FileSizeLimit?.cardImage1.allowedTypes}
                                            preview={
                                                imageDataUrls.cardImagePreview
                                            }
                                            tooltip={FileSizeLimit?.cardImage1.tooltipItems as any}
                                            fileSizeLimit={FileSizeLimit?.cardImage1}
                                            required
                                        />
                                        <FileInputUploadField
                                            label="Card Image (2)"
                                            fieldName="cardImage2"
                                            register={register('cardImage2') as any}
                                            errors={errors}
                                            existingFile={
                                                imageDataUrls.existingCardImage2
                                            }
                                            watchFile={
                                                watchedCardImage2 as
                                                | File
                                                | null
                                                | any
                                            }
                                            onFileSelect={(file) =>
                                                handleImageSelect(
                                                    'cardImage2',
                                                    file,
                                                    'cardImagePreview2',
                                                    'existingCardImage2',
                                                )
                                            }
                                            allowedTypes={FileSizeLimit?.cardImage2?.allowedTypes}
                                            preview={
                                                imageDataUrls.cardImagePreview2
                                            }
                                            tooltip={FileSizeLimit?.cardImage2.tooltipItems as any}
                                            fileSizeLimit={FileSizeLimit?.cardImage2}
                                            required
                                        />

                                        <FileInputUploadField
                                            label="Banner Image"
                                            fieldName="bannerImage"
                                            register={register('bannerImage') as any}
                                            errors={errors}
                                            existingFile={
                                                imageDataUrls.existingBanner
                                            }
                                            watchFile={
                                                watchedBanner as
                                                | File
                                                | null
                                                | any
                                            }
                                            onFileSelect={(file) =>
                                                handleImageSelect(
                                                    'bannerImage',
                                                    file,
                                                    'bannerPreview',
                                                    'existingBanner',
                                                )
                                            }
                                            allowedTypes={FileSizeLimit?.banner_image?.allowedTypes}
                                            preview={
                                                imageDataUrls.bannerPreview
                                            }
                                            tooltip={FileSizeLimit?.banner_image?.tooltipItems as any}
                                            fileSizeLimit={FileSizeLimit?.banner_image}
                                        />
                                    </div>
                                </div>

                                {/* LINKED ENTITIES (RIGHT - 50%) */}
                                <div className="bg-white dark:bg-slate-900 border border-dashed border-gray-300 dark:border-slate-700 rounded-lg p-6">
                                    <h4 className="font-semibold text-gray-800 dark:text-gray-200 mb-1">
                                        📦 Linked Content
                                    </h4>
                                    <p className="text-xs text-gray-500 mb-4">
                                        Map shows and episodes to this collection
                                    </p>

                                    <EpisodeForm
                                        initialData={allEntities}
                                        onEpisodesChange={handleEpisodesChange}
                                    />
                                </div>
                            </div>

                            {/* STICKY ACTION BAR */}
                            <div className="sticky bottom-0 bg-white dark:bg-slate-800 border-t border-gray-200 dark:border-slate-700 p-4 flex justify-end gap-3 z-10">
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    className="px-6 py-2 bg-gray-300 dark:bg-slate-700 rounded"
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                >
                                    {loading ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </FormProvider>
                </SmoothTransition>
            )}
        </div>
    )
}

export default AddUpdateCollection
